import type { LookupResult } from '../api/workspaceApi';

/**
 * Кэш ответов живого словарного поиска на уровне модуля (а не инстанса хука).
 *
 * Вынесен из `useTranslationLookup` по двум причинам:
 * 1. Его нужно сбрасывать ИЗВНЕ после создания карточки — иначе повторный ввод того
 *    же слова отдаст старый ответ, где `alreadyAdded` ещё пуст, и юзер выберет уже
 *    добавленный смысл (получит 409 вместо честной галочки «уже учится»).
 * 2. Отдельный модуль разрывает цикл импортов: `workspaceApi` зовёт `forgetLookup`,
 *    а сам хук импортит `lookupTranslation` из `workspaceApi` (здесь только
 *    `import type`, он стирается при компиляции).
 */
const cache = new Map<string, LookupResult>();

const normalize = (word: string) => word.trim().toLowerCase();
// Пометки строк («уже учится», «генерируется», «+AI») считаются для модуля.
const key = (word: string, moduleId: string | null) => `${moduleId ?? ''}|${normalize(word)}`;

export function getCachedLookup(word: string, moduleId: string | null): LookupResult | undefined {
  return cache.get(key(word, moduleId));
}

export function setCachedLookup(word: string, moduleId: string | null, result: LookupResult): void {
  cache.set(key(word, moduleId), result);
}

const forgetListeners = new Set<(word: string) => void>();

/** Подписка на сброс слова: открытый поиск этого слова перезапрашивает ответ, чтобы
 *  «создаётся» сменилось на «учишь» сразу после уведомления, без перепечатывания. */
export function subscribeLookupForget(listener: (word: string) => void): () => void {
  forgetListeners.add(listener);
  return () => {
    forgetListeners.delete(listener);
  };
}

/** Забыть ответы по слову во всех модулях — после создания карточки (ручной или AI)
 *  и после события генерации (часть речи добавилась или не добавилась). */
export function forgetLookup(word: string): void {
  const normalized = normalize(word);
  const suffix = `|${normalized}`;
  for (const k of [...cache.keys()]) {
    if (k.endsWith(suffix)) cache.delete(k);
  }
  forgetListeners.forEach((listener) => listener(normalized));
}
